import {
  AfterViewInit, ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {PatientResponsiblePartyViewModel} from '../../../../shared/models/ocl';
import {ScreenService} from '../../../../shared/services/general';

@Component({
  selector: 'app-patient-responsible-parties',
  templateUrl: './patient-responsible-parties.component.html',
  styleUrls: ['./patient-responsible-parties.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientResponsiblePartiesComponent implements OnInit, AfterViewInit {
  @Input() public responsibleParties: PatientResponsiblePartyViewModel[] = [];
  @Input() public maxHeight: string | number = '100%';
  @Input() public gridHeight: number | string = '100%';
  @ViewChild('container', {static: true}) public containerDiv?: ElementRef;
  @ViewChild('controls', {static: true}) public controlsDiv?: ElementRef;
  public isEditorVisible = false;
  public selectedRowKeys: PatientResponsiblePartyViewModel[] = [];
  @Output() public responsiblePartiesChanged = new EventEmitter();
  public gridSize: number = 120;
  public isPhone: boolean = false;
  public isPhoneOrTablet: boolean = false;
  public responsiveButtons: any;
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(private screen: ScreenService, private ref: ChangeDetectorRef) {
    this.responsiveButtons = [{
      name: 'Add',
      icon: 'add'
    }, {
      name: 'Remove',
      icon: 'trash',
      disabled: true
    }, {
      name: 'Up',
      icon: 'arrowup',
      disabled: true
    }, {
      name: 'Down',
      icon: 'arrowdown',
      disabled: true
    },
    ];
  }

  public showForm() {
    this.isEditorVisible = true;
  }

  public ngOnInit(): void {
    this.gridSize = this.GridSize;

    this.isPhone = this.screen.sizes['screen-x-small'];
    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.isPhone = this.screen.sizes['screen-x-small'];
      this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
      this.refresh();
    });
  }

  public get GridSize() {
    const element = this.containerDiv?.nativeElement as HTMLElement;
    return element?.getBoundingClientRect().height -
      32 - element?.getBoundingClientRect().height;
  }

  public formClosed(responsibleParty: PatientResponsiblePartyViewModel) {
    this.isEditorVisible = false;
    if (responsibleParty) {
      responsibleParty.order = responsibleParty.order ? responsibleParty.order : this.getNextOrder();
      this.responsibleParties.push(responsibleParty);
      this.responsiblePartiesChanged.emit(this.responsibleParties);
    }
    this.refresh();
  }

  public getNextOrder() {
    if (!this.responsibleParties) {
      return 1;
    }
    return this.responsibleParties.length > 0 ?
      Math.max(...this.responsibleParties.map(r => r.order)) + 1 :
      1;
  }

  public changeUp() {
    const prev = Math.min(...this.getIndexesOfSelected()) - 1;

    if (prev >= 0) {
      this.responsibleParties[prev].order = this.responsibleParties[Math.max(...this.getIndexesOfSelected())].order;
      this.getIndexesOfSelected().forEach(index => this.responsibleParties[index].order--);
      this.responsibleParties.sort((a, b) => a.order - b.order);
      this.responsiblePartiesChanged.emit(this.responsibleParties);
    }
    this.refresh();
  }

  private getIndexesOfSelected() {
    const indexes: number[] = [];

    this.selectedRowKeys.forEach(rp => {
      // tslint:disable-next-line:max-line-length
      const index = this.responsibleParties.findIndex(r => r.firstName === rp.firstName && r.lastLame === rp.lastLame && r.address === rp.address && r.fax === rp.fax && r.mobile === rp.mobile && r.phone === rp.phone);
      indexes.push(index);
    });

    return indexes;
  }

  public changeDown() {
    const prev = Math.max(...this.getIndexesOfSelected()) + 1;

    if (prev < this.responsibleParties.length) {
      this.responsibleParties[prev].order = this.responsibleParties[Math.min(...this.getIndexesOfSelected())].order;
      this.getIndexesOfSelected().forEach(index => this.responsibleParties[index].order++);
      this.responsibleParties.sort((a, b) => a.order - b.order);
      this.responsiblePartiesChanged.emit(this.responsibleParties);
    }
    this.refresh();
  }

  public removeClicked() {
    this.selectedRowKeys.forEach(rp => {
      // tslint:disable-next-line:max-line-length
      const index = this.responsibleParties.findIndex(r => r.firstName === rp.firstName && r.lastLame === rp.lastLame && r.address === rp.address && r.fax === rp.fax && r.mobile === rp.mobile && r.phone === rp.phone);
      this.responsibleParties.splice(index, 1);
    });

    this.responsiblePartiesChanged.emit(this.responsibleParties);
    this.selectedRowKeys = [];
    this.refresh();
  }

  public ngAfterViewInit(): void {
    this.selectedRowKeys = [];

    if (typeof this.maxHeight === 'number' && this.controlsDiv) {
      this.gridHeight = this.maxHeight - this.controlsDiv.nativeElement.clientHeight - 32;
    } else {
      this.gridHeight = '100%';
    }

    this.refresh();
  }

  public onItemClick(e: any) {
    switch (e.itemData.name) {
      case 'Up':
        this.changeUp();
        break;
      case 'Down':
        this.changeDown();
        break;
      case 'Add':
        this.showForm();
        break;
      case 'Remove':
        this.removeClicked();
        break;
    }
  }

  public selectionChange() {
    setTimeout(() => {
      this.changeStateButtons();
    }, 1000);
  }

  public changeStateButtons() {
    this.responsiveButtons = [{
      name: 'Add',
      icon: 'add'
    }, {
      name: 'Remove',
      icon: 'trash',
      disabled: this.selectedRowKeys.length === 0
    }, {
      name: 'Up',
      icon: 'arrowup',
      disabled: this.disableUp
    }, {
      name: 'Down',
      icon: 'arrowdown',
      disabled: this.disableDown
    }
    ];
    this.refresh();
  }

  public refresh() {
    if (this.ref) {
      this.ref.detectChanges();
    }
  }

  public get disableUp() {
    return this.selectedRowKeys.length !== 1 || this.responsibleParties === undefined || this.responsibleParties.length <= 1 ||
      (this.selectedRowKeys[0] as any).order === 1;
  }

  public get disableDown() {
    return this.selectedRowKeys.length !== 1 || this.responsibleParties === undefined || this.responsibleParties.length <= 1 ||
      (this.selectedRowKeys[0] as any).order === this.responsibleParties.length;
  }
}
